import React, { useEffect, useState } from "react";
import { FleetrifyBgs } from "../../../assets/video";
import { FleetrifyInputField } from "../../../components/Input/input";
import { fleetrifyIcons } from "../../../assets/icons/icons";
import { Eye, EyeOff, Lock, ShieldAlert, X } from "lucide-react";
import { formatCountdown, useLoginControllers } from "../../../controllers/useLoginControllers";
import { FleetrifyButton } from "../../../components/Button/customButton";
import { FLEETRIFYROUTEPATH } from "../../../routes/routesPath";
import { Link } from "react-router-dom";
import CarouselCard from "../../../components/Carousel";

const LoginPage = () => {
  const [securityBanner, setSecurityBanner] = useState(false);

  useEffect(() => {
    const reason = localStorage.getItem("fleetrify_logout_reason");
    if (reason === "session_revoked") {
      setSecurityBanner(true);
      localStorage.removeItem("fleetrify_logout_reason");
    }
  }, []);
  const slides = [
    {
      title: "Welcome to Fleetrify",
      description: "Login to manage logistics and fleet operations",
    },
    {
      title: "Real-time Fleet Insights",
      description: "Get live updates and analytics on every vehicle in your fleet",
    },
    {
      title: "Simplify Logistics Management",
      description: "Automate routing, tracking, and reporting — all in one platform",
    },
  ];

  const {
    form,
    showPassword,
    isDisabled,
    isLoading,
    isLocked,
    lockSecondsLeft,
    lockMessage,
    toggleShowPassword,
    handleSubmit,
    handleChange,
  } = useLoginControllers();

  const { email, password } = form;

  return (
    <div className="relative flex flex-col lg:flex-row min-h-screen items-center justify-center lg:bg-transparent bg-gray-100">
      {/*video section */}
      <div className="relative w-full lg:w-[45%] h-screen lg:h-screen order-2 lg:order-none">
        <video
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
        >
            <source src={FleetrifyBgs} type="video/mp4" />
          Your browser does not support the video tag.
        </video>

        <div className="absolute inset-0 bg-black opacity-50 z-10"></div>

        <div>
          <CarouselCard slides={slides} />
        </div>
      </div>

      {/* login Form */}
      <div className="w-full lg:w-[55%] flex justify-center items-center absolute lg:static z-30 px-5 top-0 bottom-0 my-auto">
        <form
          onSubmit={handleSubmit}
          className="w-[430px] sm:w-[340px] md:w-[500px] lg:w-[430px] max-w-full bg-white px-4 py-6 rounded"
        >
          <div className="space-y-6">
            {securityBanner && (
              <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                <ShieldAlert size={18} className="flex-shrink-0 mt-0.5 text-red-500" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-red-700">Security Alert</p>
                  <p className="text-xs text-red-600 mt-0.5 leading-relaxed">
                    Your account was signed in from another device or location. You have been automatically logged out to protect your account.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setSecurityBanner(false)}
                  className="flex-shrink-0 text-red-400 hover:text-red-600 transition-colors"
                  aria-label="Dismiss"
                >
                  <X size={15} />
                </button>
              </div>
            )}

            <div className="px-8 space-y-2">
              <h2 className="text-2xl font-bold text-center text-[#143887]">
                Welcome Back!
              </h2>
              <p className=" text-base text-center mb-8">
                Enter your credentials to login
              </p>
            </div>

            {/*email input */}
            <div className="space-y-4">
              <div>
                <label className="block text-[#4F4F4F] mb-1 font-bold text-base">
                  Email
                </label>
                <FleetrifyInputField
                  value={email}
                  onChange={(e) => handleChange(e.target.value, "email")}
                  type="email"
                  id="email"
                  icon={fleetrifyIcons.mailIcon}
                  required
                  placeholder="Enter Email"
                  className="mt-1"
                />
              </div>

              {/* Password Input */}
              <div className="relative">
                <label className="block text-[#4F4F4F] mb-1 font-bold text-base">
                  Password
                </label>
                <FleetrifyInputField
                  icon={
                    <button type="button" onClick={toggleShowPassword}>
                      {showPassword ? (
                        <EyeOff color="#333333" size={16} />
                      ) : (
                        <Eye color="#333333" size={16} />
                      )}
                    </button>
                  }
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => handleChange(e.target.value, "password")}
                  placeholder="Enter password"
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          <div className="text-right pt-1">
            <Link
              to={FLEETRIFYROUTEPATH.FORGOT_PASSWORD}
              className="text-xs text-[#2F80ED] hover:text-blue-700 transition-colors"
            >
              Forgot password?
            </Link>
          </div>

          {/* Account locked after too many wrong passwords */}
          {isLocked && (
            <div
              role="alert"
              className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mt-4"
            >
              <Lock size={18} className="flex-shrink-0 mt-0.5 text-red-500" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-red-600 leading-relaxed">{lockMessage}</p>
                <Link
                  to={FLEETRIFYROUTEPATH.FORGOT_PASSWORD}
                  className="inline-block mt-1 text-xs font-semibold text-[#2F80ED] hover:text-blue-700 transition-colors"
                >
                  Reset your password
                </Link>
              </div>
            </div>
          )}

          {/* Submit */}
          <FleetrifyButton
            type="submit"
            className="w-full mt-6"
            disabled={isDisabled || isLoading}
          >
            {isLoading
              ? "Loading..."
              : isLocked
              ? `Try again in ${formatCountdown(lockSecondsLeft)}`
              : "Submit"}
          </FleetrifyButton>

          <div className="text-center pt-2">
            <span>Don't have an account?</span>{" "}
            <Link
              to={FLEETRIFYROUTEPATH.SIGN_UP}
              className=" text-[#2F80ED] text-bold hover:text-blue-700 transition-colors"
            >
              Sign Up
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
